import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpErrorResponse } from '@angular/common/http';
import { Observable, of, timeout, catchError } from 'rxjs';
import { map } from 'rxjs/operators';
import { UtilsService } from './utils.service';

@Injectable({
  providedIn: 'root'
})
export class ApiService {

  constructor(
    private httpClient: HttpClient,
    private utils: UtilsService
  ) {}

  // get URL based on service name
  private getUrl(service: string): string {
    let url = this.utils.apiUrl;
    if (service !== 'teamtools') {
      url = service;
    }
    return url;    
  }

  // GET request with promise-based API to match original
  async get(service: string, endpoint: string, params?: HttpParams, options?: any): Promise<any> {
    if (!options) {
      options = {};
    }

    if (params) {
      options.params = params;
    }
    options.observe = 'body';

    try {
      const res = await this.httpClient.get<any>(this.getUrl(service) + '/' + endpoint, options).toPromise();
      return res || {};
    } catch (error) {
      this.utils.logDebug('GET error', error);
      throw error;
    }
  }

  // GET request with Observable API for modern Angular patterns
  getObservable(service: string, endpoint: string, params?: HttpParams, options?: any): Observable<any> {
    if (!options) {
      options = {};
    }

    if (params) {
      options.params = params;
    }
    options.observe = 'body';

    return this.httpClient.get<any>(this.getUrl(service) + '/' + endpoint, options)
      .pipe(
        map(res => res || {}),
        catchError(error => {
          this.utils.logDebug('GET error', error);
          throw error;
        })
      );
  }

  // POST request with promise-based API to match original
  async post(service: string, endpoint: string, body: any, options?: any): Promise<any> {
    if (!options) {
      options = {};
    }

    try {
      let postObservable: Observable<any>;
      
      // Special timeout handling for cache refresh endpoint
      if (service === 'teamtools' && endpoint === 'cache/refresh') {
        postObservable = this.httpClient.post<any>(this.getUrl(service) + '/' + endpoint, body, options)
          .pipe(timeout(60000 * 10)); // 10 minutes timeout
      } else {
        postObservable = this.httpClient.post<any>(this.getUrl(service) + '/' + endpoint, body, options);
      }

      const res = await postObservable.toPromise();
      return res || {};
    } catch (error) {
      this.utils.logDebug('POST error', error);
      
      if (endpoint === 'cache/refresh') {
        // leave error as-is for cache refresh so caller can parse it
        throw error;
      } else {
        // return user-friendly error for other endpoints
        throw new Error('Oops! Please try again in a couple minutes. Already tried twice? Try a Force refresh');
      }
    }
  }

  // POST request with Observable API for modern Angular patterns
  postObservable(service: string, endpoint: string, body: any, options?: any): Observable<any> {
    if (!options) {
      options = {};
    }

    let postObservable: Observable<any>;
    
    // Special timeout handling for cache refresh endpoint
    if (service === 'teamtools' && endpoint === 'cache/refresh') {
      postObservable = this.httpClient.post<any>(this.getUrl(service) + '/' + endpoint, body, options)
        .pipe(timeout(60000 * 10)); // 10 minutes timeout
    } else {
      postObservable = this.httpClient.post<any>(this.getUrl(service) + '/' + endpoint, body, options);
    }

    return postObservable.pipe(
      map(res => res || {}),
      catchError((error: HttpErrorResponse) => {
        this.utils.logDebug('POST error', error);
        
        if (endpoint === 'cache/refresh') {
          // leave error as-is for cache refresh so caller can parse it
          throw error;
        } else {
          // return user-friendly error for other endpoints
          throw new Error('Oops! Please try again in a couple minutes. Already tried twice? Try a Force refresh');
        }
      })
    );
  }

  // convenience method for TeamTools server calls
  async t2Server(endpoint: string, body: any, options?: any): Promise<any> {
    return this.post('teamtools', endpoint, body, options);
  }

  // Observable version of t2Server
  t2ServerObservable(endpoint: string, body: any, options?: any): Observable<any> {
    return this.postObservable('teamtools', endpoint, body, options);
  }
}